import { Box, Grid, Stack } from "@mui/material";
import Input from "components/Input";
import PhoneInput from "components/PhoneInput";
import { COUNTRY_ISO3_CODES } from "constants/index";
import { validatePhoneNumber } from "helpers/utils";
import _ from "lodash";
import phone from "phone";
import { Controller, useFormContext } from "react-hook-form";

const ContactsTab = () => {
  const formContext = useFormContext();
  const {
    control,
    watch,
    formState: { errors },
  } = formContext;
  const watchCountry = watch("country");

  return (
    <Grid container spacing={6}>
      <Grid item xs={6}>
        <h2>Primary Emergency Contact</h2>
        <Stack gap={2} mt={`20px`}>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Name
            </Box>
            <Controller
              name="primary_contact_name"
              control={control}
              rules={{ required: false }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <Input value={value} onChange={onChange} maxLength={50} />
                </Stack>
              )}
            />
          </Stack>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Relationship
            </Box>
            <Controller
              name="primary_contact_relationship"
              control={control}
              rules={{ required: false }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <Input value={value} onChange={onChange} maxLength={20} />
                </Stack>
              )}
            />
          </Stack>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Phone
            </Box>
            <Controller
              name="primary_contact_phone"
              control={control}
              rules={{
                maxLength: 20,
                required: false,
                validate: (value: string) => {
                  return validatePhoneNumber(value, watchCountry);
                },
              }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <div className="base-input">
                    <PhoneInput
                      iso2={COUNTRY_ISO3_CODES[watchCountry]?.iso2}
                      international={true}
                      withCountryCallingCode={true}
                      value={value}
                      onChange={onChange}
                      hasError={!_.isEmpty(errors.primary_contact_phone)}
                    />
                  </div>

                  {errors.primary_contact_phone?.type === "validate" && (
                    <>
                      <h4 className="error">Please add a valid phone number.</h4>
                      <p className="phone-hint">{COUNTRY_ISO3_CODES[watchCountry]?.hint || ""}</p>
                    </>
                  )}
                  {errors.primary_contact_phone?.type === "maxLength" && (
                    <>
                      <h4 className="error">Length can not be greater than 20.</h4>
                    </>
                  )}
                </Stack>
              )}
            />
          </Stack>
        </Stack>
      </Grid>
      <Grid item xs={6}>
        <h2>Secondary Emergency Contact</h2>
        <Stack gap={2} mt={`20px`}>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Name
            </Box>
            <Controller
              name="secondary_contact_name"
              control={control}
              rules={{ required: false }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <Input value={value} onChange={onChange} maxLength={50} />
                </Stack>
              )}
            />
          </Stack>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Relationship
            </Box>
            <Controller
              name="secondary_contact_relationship"
              control={control}
              rules={{ required: false }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <Input value={value} onChange={onChange} maxLength={20} />
                </Stack>
              )}
            />
          </Stack>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Phone
            </Box>
            <Controller
              name="secondary_contact_phone"
              control={control}
              rules={{
                maxLength: 20,
                required: false,
                validate: (value: string) => {
                  return validatePhoneNumber(value, watchCountry);
                },
              }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <div className="base-input">
                    <PhoneInput
                      iso2={COUNTRY_ISO3_CODES[watchCountry]?.iso2}
                      international={true}
                      withCountryCallingCode={true}
                      value={value}
                      onChange={onChange}
                      hasError={!_.isEmpty(errors.secondary_contact_phone)}
                    />
                  </div>

                  {errors.secondary_contact_phone?.type === "validate" && (
                    <>
                      <h4 className="error">Please add a valid phone number.</h4>
                      <p className="phone-hint">{COUNTRY_ISO3_CODES[watchCountry]?.hint || ""}</p>
                    </>
                  )}
                  {errors.secondary_contact_phone?.type === "maxLength" && (
                    <>
                      <h4 className="error">Length can not be greater than 20.</h4>
                    </>
                  )}
                </Stack>
              )}
            />
          </Stack>
        </Stack>
      </Grid>
      <Grid item xs={6}>
        <h2>Physician</h2>
        <Stack gap={2} mt={`20px`}>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Name
            </Box>
            <Controller
              name="physician_name"
              control={control}
              rules={{ required: false }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <Input value={value} onChange={onChange} maxLength={50} />
                </Stack>
              )}
            />
          </Stack>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Specialty
            </Box>
            <Controller
              name="physician_speciality"
              control={control}
              rules={{ maxLength: 20, required: false }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <Input
                    value={value}
                    onChange={onChange}
                    hasError={!_.isEmpty(errors.physician_speciality)}
                    maxLength={20}
                  />
                  {errors.physician_speciality?.type === "maxLength" && (
                    <h4 className="error">Length can not exceed 20.</h4>
                  )}
                </Stack>
              )}
            />
          </Stack>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Phone
            </Box>
            <Controller
              name="physician_phone"
              control={control}
              rules={{
                maxLength: 20,
                required: false,
                validate: (value: string) => {
                  return validatePhoneNumber(value, watchCountry);
                },
              }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <div className="base-input">
                    <PhoneInput
                      iso2={COUNTRY_ISO3_CODES[watchCountry]?.iso2}
                      international={true}
                      withCountryCallingCode={true}
                      value={value}
                      onChange={onChange}
                      hasError={!_.isEmpty(errors.physician_phone)}
                    />
                  </div>

                  {errors.physician_phone?.type === "validate" && (
                    <>
                      <h4 className="error">Please add a valid phone number.</h4>
                      <p className="phone-hint">{COUNTRY_ISO3_CODES[watchCountry]?.hint || ""}</p>
                    </>
                  )}
                  {errors.physician_phone?.type === "maxLength" && (
                    <>
                      <h4 className="error">Length can not be greater than 20.</h4>
                    </>
                  )}
                </Stack>
              )}
            />
          </Stack>
        </Stack>
      </Grid>
      <Grid item xs={6}>
        <h2>Other Contact Information</h2>
        <Stack gap={2} mt={`20px`}>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Name
            </Box>
            <Controller
              name="other_contact_name"
              control={control}
              rules={{ required: false }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <Input value={value} onChange={onChange} maxLength={50} />
                </Stack>
              )}
            />
          </Stack>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Relationship
            </Box>
            <Controller
              name="other_contact_relationship"
              control={control}
              rules={{ required: false }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <Input value={value} onChange={onChange} maxLength={20} />
                </Stack>
              )}
            />
          </Stack>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Phone
            </Box>
            <Controller
              name="other_contact_phone"
              control={control}
              rules={{
                maxLength: 20,
                required: false,
                validate: (value: string) => {
                  return validatePhoneNumber(value, watchCountry);
                },
              }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <div className="base-input">
                    <PhoneInput
                      iso2={COUNTRY_ISO3_CODES[watchCountry]?.iso2}
                      international={true}
                      withCountryCallingCode={true}
                      value={value}
                      onChange={onChange}
                      hasError={!_.isEmpty(errors.other_contact_phone)}
                    />
                  </div>

                  {errors.other_contact_phone?.type === "validate" && (
                    <>
                      <h4 className="error">Please add a valid phone number.</h4>
                      <p className="phone-hint">{COUNTRY_ISO3_CODES[watchCountry]?.hint || ""}</p>
                    </>
                  )}
                  {errors.other_contact_phone?.type === "maxLength" && (
                    <>
                      <h4 className="error">Length can not be greater than 20.</h4>
                    </>
                  )}
                </Stack>
              )}
            />
          </Stack>
        </Stack>
      </Grid>
    </Grid>
  );
};

export default ContactsTab;
